import {
  bookingRefundOffers,
  bookingRefundOffersPatch,
  bookingReleaseOffers,
  bookingReleaseOffersPatch,
} from "../../app/api/cancelBooking";
import { calculatePrice } from "../../Lib/Utils/validationUtil";
import { extractBookedNolAncillaries } from "../../Lib/Utils/nolCard";
import {
  AdmissionStatusEnum,
  AncillaryStatusEnum,
} from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookingResponse } from "../../Orchestrator/Types/Index";
import {
  IBookingRefundOffersResponse,
  IBookingReleaseOffersResponse,
  IRefundOffer,
  IReleaseOffer,
} from "../../Orchestrator/Types/Types/BookingTypes";

/** What the confirm dialog shows before the refund is committed. */
export interface OnwardRefundQuote {
  fulfillmentIds: string[];
  refundableAmount: number;
  refundFee: number;
  /** refundableAmount + refundFee — what was originally paid for these fulfillments. */
  paidAmount: number;
  /** Shuttles hold admissions (a travel right) and must be released first. */
  needsRelease: boolean;
}

/**
 * Backend settle-order race guard, copied verbatim from ConfirmCancelClickAction:
 * the release/refund PATCHes 500-error when fired back-to-back.
 * ponytail: fixed sleep, replace with a status poll if the backend ever exposes one.
 */
const SETTLE_DELAY_MS = 500;
const settleDelay = () => new Promise((resolve) => setTimeout(resolve, SETTLE_DELAY_MS));

/**
 * Fulfillment ids for a booked shuttle offer, matched the same way the live
 * cancel path does: an admission's `id` IS the fulfillment id, so intersect
 * the offer's FULFILLED admissions with `booking.fulfillments`.
 *
 * Empty result = nothing paid for on this offer (freshly added in checkout,
 * or an add whose payment was abandoned) — the caller should plain-DELETE it
 * instead of refunding.
 */
export function shuttleFulfillmentIds(
  booking: IBookingResponse | null,
  offerId: string
): string[] {
  if (!offerId) return [];
  const offers = booking?.booking?.bookedOffers || [];
  const offer = offers.find((o) => o?.offerId === offerId);
  if (!offer) return [];
  const fulfillmentIds = new Set(
    (booking?.booking?.fulfillments || []).map((f) => f?.id).filter(Boolean)
  );
  return (offer.admissions || [])
    .filter(
      (admission) =>
        admission?.status === AdmissionStatusEnum.FULFILLED &&
        admission?.id &&
        fulfillmentIds.has(admission.id)
    )
    .map((admission) => admission.id);
}

/**
 * Fulfillment ids for the leg's booked Nol passes. Nol is an ancillary, not an
 * admission — it never appears in `booking.fulfillments`, but the backend
 * accepts ancillary ids as fulfillment ids on the refund endpoints (same
 * convention ProbeRefundOffersAction relies on).
 */
/**
 * True when the leg's Nol passes exist but NONE of them are FULFILLED — i.e.
 * attached to the booking and never paid for. Plain DELETE is safe there (it
 * refunds nothing because nothing was paid), and it is the only way out of an
 * otherwise stuck card: no fulfillments means no refund quote, and the
 * aftersales path refuses to guess.
 */
/**
 * True when the shuttle offer has admissions but they are all RELEASED — the
 * signature of a refund that died between the release PATCH and the refund
 * PATCH. The passenger has lost the travel right AND the money, and no retry
 * can recover it from the UI because RELEASED admissions never appear in
 * `booking.fulfillments`. Callers must say so specifically rather than showing
 * a generic error the passenger will keep retrying.
 */
export function shuttleIsReleasedNotRefunded(
  booking: IBookingResponse | null,
  offerId: string
): boolean {
  if (!offerId) return false;
  const offer = (booking?.booking?.bookedOffers || []).find((o) => o?.offerId === offerId);
  const admissions = offer?.admissions || [];
  return (
    admissions.length > 0 &&
    admissions.every((a) => (a as { status?: string })?.status === AdmissionStatusEnum.RELEASED)
  );
}

export function nolIsUnpaidOnly(
  booking: IBookingResponse | null,
  legId: string
): boolean {
  if (!legId) return false;
  const legAncillaryIds = new Set(
    extractBookedNolAncillaries(booking, legId).map((a) => a.ancillaryId)
  );
  if (legAncillaryIds.size === 0) return false;
  const legStatuses: string[] = [];
  for (const offer of booking?.booking?.bookedOffers || []) {
    for (const anc of (offer as any)?.ancillaries || []) {
      if (anc?.id && legAncillaryIds.has(anc.id)) legStatuses.push(anc.status);
    }
  }
  return (
    legStatuses.length > 0 &&
    legStatuses.every((st) => st !== AncillaryStatusEnum.FULFILLED)
  );
}

export function nolFulfillmentIds(
  booking: IBookingResponse | null,
  legId: string
): string[] {
  if (!legId) return [];
  const statusById = new Map<string, string>();
  for (const offer of booking?.booking?.bookedOffers || []) {
    for (const anc of (offer as any)?.ancillaries || []) {
      if (anc?.id) statusById.set(anc.id, anc.status);
    }
  }
  return extractBookedNolAncillaries(booking, legId)
    .map((a) => a.ancillaryId)
    .filter((id) => statusById.get(id) === AncillaryStatusEnum.FULFILLED);
}

const sumRefundOffers = (offers: IRefundOffer[]) =>
  offers.reduce(
    (acc, offer) => ({
      refundableAmount: acc.refundableAmount + (calculatePrice(offer?.refundableAmount) || 0),
      refundFee: acc.refundFee + (calculatePrice(offer?.refundFee) || 0),
    }),
    { refundableAmount: 0, refundFee: 0 }
  );

/**
 * Price the cancellation WITHOUT committing it. POST /refund-offers only
 * generates offers — nothing settles until the PATCH — which is what lets
 * ProbeRefundOffersAction fire it speculatively on every manage-seating entry.
 *
 * Returns null when the backend produced no refund offer, so the caller can
 * surface an error instead of showing a misleading "you get back AED 0".
 */
export async function quoteOnwardRefund(
  bookingId: string,
  fulfillmentIds: string[],
  needsRelease: boolean
): Promise<OnwardRefundQuote | null> {
  // OSDM 400s on an empty fulfillmentIds array — never POST one.
  if (!bookingId || fulfillmentIds.length === 0) return null;

  const response: any = await bookingRefundOffers(
    { fulfillmentIds, overruleCode: null, refundDate: new Date().toISOString() },
    bookingId
  );
  if (response?.code !== 200) return null;

  const answer: IBookingRefundOffersResponse = response.answer;
  const offers = answer?.refundOffers || [];
  if (offers.length === 0) return null;

  const { refundableAmount, refundFee } = sumRefundOffers(offers);
  return {
    fulfillmentIds,
    refundableAmount,
    refundFee,
    paidAmount: refundableAmount + refundFee,
    needsRelease,
  };
}

/**
 * Commit the cancellation, mirroring ConfirmCancelClickAction's proven order:
 * release the travel right (shuttle only), then refund the money.
 *
 * The refund offers are re-POSTed rather than reusing the quote's ids: for a
 * shuttle the release in between invalidates them, and for Nol a second POST
 * is a cheap way to keep one code path. Throws on any non-200 so the caller
 * keeps the card on screen instead of clearing it against a failed refund.
 */
export async function commitOnwardRefund(
  bookingId: string,
  quote: OnwardRefundQuote
): Promise<void> {
  const { fulfillmentIds, needsRelease } = quote;
  if (!bookingId || fulfillmentIds.length === 0) {
    throw new Error("commitOnwardRefund: missing bookingId or fulfillmentIds");
  }

  if (needsRelease) {
    const releaseResponse: any = await bookingReleaseOffers({ fulfillmentIds }, bookingId);
    if (releaseResponse?.code !== 200) {
      throw new Error("release-offers failed");
    }
    const released: IBookingReleaseOffersResponse = releaseResponse.answer;
    for (const offer of released?.releaseOffers || ([] as IReleaseOffer[])) {
      const patched: any = await bookingReleaseOffersPatch(offer.id, bookingId);
      if (patched?.code !== 200) {
        throw new Error(`release-offers PATCH failed for ${offer.id}`);
      }
      await settleDelay();
    }
    await settleDelay();
  }

  const refundResponse: any = await bookingRefundOffers(
    { fulfillmentIds, overruleCode: null, refundDate: new Date().toISOString() },
    bookingId
  );
  if (refundResponse?.code !== 200) {
    throw new Error("refund-offers failed");
  }
  const refunded: IBookingRefundOffersResponse = refundResponse.answer;
  const refundOfferIds = (refunded?.refundOffers || []).map((offer) => offer.id);
  if (refundOfferIds.length === 0) {
    throw new Error("refund-offers returned no offers");
  }
  // The PATCHes are what actually move the money — an unchecked failure here
  // turns a diagnosable API error into a silent no-op.
  for (const refundOfferId of refundOfferIds) {
    const patched: any = await bookingRefundOffersPatch(refundOfferId, bookingId);
    if (patched?.code !== 200) {
      throw new Error(`refund-offers PATCH failed for ${refundOfferId}`);
    }
    await settleDelay();
  }
}
